/**
 * Generic Multi-Select Component
 *
 * A flexible multi-select component using shadcn/ui Popover + Command pattern.
 * Replaces react-select for generic option types (not just accounts).
 *
 * Features:
 * - Generic option type support
 * - Custom option rendering
 * - Optional grouping
 * - Checkbox-based selection
 * - Search functionality
 * - Controlled component API
 */

import { useMemo, useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@/components/ui/icons/check-icon";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";

export interface BaseOption {
  value: string;
  label: string;
}

export interface OptionGroup<T extends BaseOption> {
  label: string;
  options: T[];
}

export interface MultiSelectProps<T extends BaseOption> {
  options: T[] | OptionGroup<T>[];
  value?: T[];
  onChange: (value: T[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  searchable?: boolean;
  closeMenuOnSelect?: boolean;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  renderMenuOption?: (option: T, isSelected: boolean) => React.ReactNode;
  renderTrigger?: (selectedOptions: T[]) => React.ReactNode;
  matchTriggerWidth?: boolean;
}

export function MultiSelect<T extends BaseOption>({
  options,
  value = [],
  onChange,
  placeholder = "Select items...",
  emptyMessage = "No items found.",
  className,
  disabled = false,
  searchable = true,
  closeMenuOnSelect = false,
  renderOption,
  renderMenuOption,
  renderTrigger,
  matchTriggerWidth = false,
}: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Measure trigger width when component mounts and when popover opens
  useEffect(() => {
    if (matchTriggerWidth && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open, matchTriggerWidth]);

  // Determine if options are grouped
  const isGrouped =
    options.length > 0 && options[0] != null && "options" in options[0];

  // Flatten options for easy lookup
  // Get selected option IDs
  const selectedIds = useMemo(() => (value || []).map((v) => v.value), [value]);

  const toggleOption = (option: T) => {
    const isSelected = selectedIds.includes(option.value);

    if (isSelected) {
      // Remove option
      const newValue = value.filter((v) => v.value !== option.value);
      onChange(newValue);
    } else {
      // Add option
      onChange([...value, option]);
    }

    // Close menu if closeMenuOnSelect is true
    if (closeMenuOnSelect) {
      setOpen(false);
    }
  };

  // Default trigger rendering
  const defaultTriggerContent = useMemo(() => {
    const safeValue = value || [];
    if (safeValue.length === 0) {
      return <span className="text-neutral-500">{placeholder}</span>;
    }
    if (safeValue.length === 1) {
      return <span className="text-neutral-800">{safeValue[0].label}</span>;
    }
    return (
      <span className="text-neutral-800">
        {safeValue.length} items selected
      </span>
    );
  }, [value, placeholder]);

  // Default option rendering
  const defaultRenderOption = (option: T, _isSelected: boolean) => (
    <span className="text-sm text-neutral-700">{option.label}</span>
  );

  const menuOptionRenderer =
    renderMenuOption || renderOption || defaultRenderOption;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-10 w-full justify-between",
            "font-inter text-sm font-medium",
            dropdownTriggerClassName,
            className
          )}
        >
          {renderTrigger ? renderTrigger(value) : defaultTriggerContent}
          <i
            className={cn(
              "fa-regular fa-chevron-down ml-2 h-4 w-4 shrink-0 leading-4 transition-transform duration-200",
              open && "rotate-180",
              dropdownChevronClassName
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border-neutral-100"
        disableAnimation
        style={
          matchTriggerWidth && triggerWidth > 0
            ? {
                width: `${triggerWidth}px`,
                minWidth: `${triggerWidth}px`,
              }
            : undefined
        }
        sideOffset={4}
        align="start"
      >
        <Command>
          {searchable && (
            <CommandInput
              placeholder="Search..."
              className="h-9 border-neutral-100 font-inter text-sm"
            />
          )}
          <CommandEmpty className="py-6 text-center font-inter text-sm text-neutral-500">
            {emptyMessage}
          </CommandEmpty>
          <CommandList>
            {isGrouped ? (
              // Render grouped options
              (options as OptionGroup<T>[]).map((group) => (
                <CommandGroup
                  key={group.label}
                  heading={group.label}
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-neutral-700 [&_[cmdk-group-heading]]:bg-neutral-50"
                >
                  {group.options.map((option) => {
                    const isSelected = selectedIds.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        keywords={[option.label]}
                        onSelect={() => toggleOption(option)}
                        aria-label={`${option.label}, ${isSelected ? "selected" : "not selected"}`}
                        aria-selected={isSelected}
                        className="font-inter font-normal text-sm text-neutral-600 cursor-pointer data-[selected=true]:text-neutral-900 px-3 py-2"
                      >
                        {/* Visual checkbox indicator - decorative only, CommandItem handles selection semantics */}
                        <div
                          aria-hidden="true"
                          className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center mr-2 flex-shrink-0 transition-colors",
                            isSelected
                              ? "bg-blue-500 border-blue-500 text-white"
                              : "border-neutral-300"
                          )}
                        >
                          {isSelected && (
                            <CheckIcon className="size-3 text-white" />
                          )}
                        </div>
                        {menuOptionRenderer(option, isSelected)}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))
            ) : (
              // Render flat options
              <CommandGroup>
                {(options as T[]).map((option) => {
                  const isSelected = selectedIds.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      keywords={[option.label]}
                      onSelect={() => toggleOption(option)}
                      aria-selected={isSelected}
                      className="font-inter font-normal text-sm text-neutral-600 cursor-pointer data-[selected=true]:text-neutral-900 px-3 py-2"
                    >
                      {/* Visual checkbox indicator - decorative only, CommandItem handles selection semantics */}
                      <div
                        aria-hidden="true"
                        className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center mr-2 flex-shrink-0 transition-colors",
                          isSelected
                            ? "bg-blue-500 border-blue-500 text-white"
                            : "border-neutral-300"
                        )}
                      >
                        {isSelected && (
                          <CheckIcon className="size-3 text-white" />
                        )}
                      </div>
                      {menuOptionRenderer(option, isSelected)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
